import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import techopsApi, { CR_STATUS, INC_STATUS } from '../utils/techopsApi';

const BRAND = '#2563EB';
const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 18 };
const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--text)', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' };
const btn = (bg = BRAND, col = '#fff') => ({ background: bg, color: col, border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' });

const Ic = {
  List: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4h12M2 8h12M2 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  Dash: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.4" /><rect x="9.5" y="1.5" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.4" /><rect x="1.5" y="9.5" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.4" /><rect x="9.5" y="9.5" width="5" height="5" rx="1.2" stroke="currentColor" strokeWidth="1.4" /></svg>,
  Plus: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>,
  Cog: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.2" stroke="currentColor" strokeWidth="1.4" /><path d="M8 1.5v1.8M8 12.7v1.8M14.5 8h-1.8M3.3 8H1.5M12.6 3.4l-1.3 1.3M4.7 11.3l-1.3 1.3M12.6 12.6l-1.3-1.3M4.7 4.7L3.4 3.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
};

function NavItem({ icon, label, active, onClick }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 8,
      cursor: 'pointer', fontSize: 13, fontWeight: active ? 700 : 600, marginBottom: 2,
      background: active ? BRAND : 'transparent', color: active ? '#fff' : 'var(--muted2)',
    }}>
      <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
    </div>
  );
}

function StatusPill({ status }) {
  const s = CR_STATUS[status] || CR_STATUS.draft;
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, color: s.color, background: s.bg, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

// A click-to-open dropdown that supports selecting multiple values.
// Options are configured by the admin in Settings — users cannot add new ones here.
function MultiPicker({ values, onChange, options, placeholder, labelOf }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const arr = Array.isArray(values) ? values : [];
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const toggle = (v) => onChange(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  const display = arr.length ? arr.map(v => (labelOf ? labelOf(v) : v)).join(', ') : '';
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        ...inp, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        minHeight: 38, color: display ? 'var(--text)' : 'var(--muted)',
      }}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {display || (placeholder || 'Select…')}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }}>
          <path d="M2.5 4.5L6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, marginTop: 4,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto', padding: 5,
        }}>
          {(!options || !options.length) && (
            <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 10px' }}>
              No options configured — an admin can add them in Settings.
            </div>
          )}
          {(options || []).map(o => {
            const val = o.value !== undefined ? o.value : o.id;
            const text = labelOf ? labelOf(val) : o.value;
            const on = arr.includes(val);
            return (
              <label key={o.id || val} style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '7px 10px', borderRadius: 6,
                cursor: 'pointer', fontSize: 13, background: on ? 'rgba(37,99,235,0.08)' : 'transparent',
              }}>
                <input type="checkbox" checked={on} onChange={() => toggle(val)} />
                <span style={{ flex: 1 }}>{text}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Multi-user dropdown picker (click to open, tick people).
function UserPicker({ values, onChange, members, placeholder }) {
  const opts = members.map(m => ({ id: m.id, value: m.id }));
  const labelOf = (id) => (members.find(m => m.id === id) || {}).full_name || id;
  return <MultiPicker values={values} onChange={onChange} options={opts} placeholder={placeholder || 'Select people…'} labelOf={labelOf} />;
}

function parseArr(v) { if (Array.isArray(v)) return v; try { return JSON.parse(v || '[]'); } catch (e) { return []; } }
function toLocal(v) { try { const d = new Date(v); if (isNaN(d)) return typeof v === 'string' ? v.slice(0, 16) : ''; const off = d.getTimezoneOffset(); return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16); } catch (e) { return ''; } }

// A textarea that continues numbered / bulleted lists automatically, the way Word does:
// type "1. Do this" and press Enter → the next line starts with "2. " ready to type.
// Pressing Enter on an empty numbered line ends the list instead of adding another number.
// If `onPasteImage` is given, pasting a picture uploads it and drops it in on its own line.
function ListTextarea({ value, onChange, style, onPasteImage, ...rest }) {
  const [busy, setBusy] = useState(false);
  const onKeyDown = (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const el = e.target;
    const pos = el.selectionStart;
    const upto = String(value || '').slice(0, pos);
    const lineStart = upto.lastIndexOf('\n') + 1;
    const line = upto.slice(lineStart);
    const numMatch = line.match(/^(\s*)(\d+)([.)])(\s+)(.*)$/);
    const bulMatch = line.match(/^(\s*)([-•*])(\s+)(.*)$/);
    if (!numMatch && !bulMatch) return;
    const content = numMatch ? numMatch[5] : bulMatch[4];
    if (!content.trim()) {
      e.preventDefault();
      const before = String(value || '').slice(0, lineStart);
      const after = String(value || '').slice(pos);
      onChange(before + after);
      requestAnimationFrame(() => { try { el.selectionStart = el.selectionEnd = before.length; } catch (err) {} });
      return;
    }
    e.preventDefault();
    let marker;
    if (numMatch) {
      const indent = numMatch[1], n = parseInt(numMatch[2], 10) + 1, sep = numMatch[3], sp = numMatch[4];
      marker = `${indent}${n}${sep}${sp}`;
    } else {
      marker = `${bulMatch[1]}${bulMatch[2]}${bulMatch[3]}`;
    }
    const before = String(value || '').slice(0, pos);
    const after = String(value || '').slice(pos);
    const insert = '\n' + marker;
    onChange(before + insert + after);
    const caret = pos + insert.length;
    requestAnimationFrame(() => { try { el.selectionStart = el.selectionEnd = caret; } catch (err) {} });
  };

  // Paste a picture straight into the text — it is uploaded and placed on its own line.
  const onPaste = async (e) => {
    if (!onPasteImage) return;
    const items = Array.from((e.clipboardData && e.clipboardData.items) || []);
    const imgItem = items.find(it => it.type && it.type.startsWith('image/'));
    if (!imgItem) return;
    const file = imgItem.getAsFile();
    if (!file) return;
    e.preventDefault();
    const el = e.target;
    const pos = el.selectionStart;
    setBusy(true);
    try {
      const marker = await onPasteImage(file);   // returns e.g. "[[img:<id>]]"
      if (!marker) return;
      const cur = String(value || '');
      const before = cur.slice(0, pos);
      const after = cur.slice(pos);
      // Put the picture on its own line.
      const pre = before && !before.endsWith('\n') ? '\n' : '';
      const post = after && !after.startsWith('\n') ? '\n' : '';
      const insert = `${pre}${marker}${post}`;
      onChange(before + insert + after);
      const caret = pos + insert.length;
      requestAnimationFrame(() => { try { el.selectionStart = el.selectionEnd = caret; } catch (err) {} });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <textarea style={style} value={value || ''} onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown} onPaste={onPaste} {...rest} />
      {busy && (
        <div style={{ position: 'absolute', right: 10, bottom: 10, fontSize: 11, fontWeight: 700, color: BRAND, background: 'var(--surface)', padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)' }}>
          Uploading picture…
        </div>
      )}
    </div>
  );
}

// Renders a text block, turning any [[img:<id>]] marker into the pasted picture.
function RichText({ text }) {
  const token = localStorage.getItem('flowup_token');
  const parts = String(text || '').split(/(\[\[img:[a-f0-9-]+\]\])/i);
  return (
    <div style={{ fontSize: 13, lineHeight: 1.6 }}>
      {parts.map((p, i) => {
        const m = p.match(/^\[\[img:([a-f0-9-]+)\]\]$/i);
        if (m) {
          return (
            <img key={i} src={`/api/techops/appendix/${m[1]}/file?token=${token}`} alt=""
              style={{ display: 'block', maxWidth: '100%', margin: '8px 0', borderRadius: 8, border: '1px solid var(--border)' }} />
          );
        }
        return <span key={i} style={{ whiteSpace: 'pre-wrap' }}>{p}</span>;
      })}
    </div>
  );
}

export default function TechOpsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [view, setView] = useState('list');
  const [current, setCurrent] = useState(null);
  const [rows, setRows] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [currentInc, setCurrentInc] = useState(null);
  const [options, setOptions] = useState({});
  const [members, setMembers] = useState([]);
  const [notifiable, setNotifiable] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 768);

  // The current view is mirrored into the URL (?view=…&cr=…&incident=…) so that a browser
  // refresh brings you back to the page you were on, rather than the first page.
  const syncUrl = useCallback((v, id) => {
    const p = new URLSearchParams();
    if (v) p.set('view', v);
    if (id && (v === 'detail' || v === 'form')) p.set('cr', id);
    if (id && (v === 'incident-detail' || v === 'incident-form')) p.set('incident', id);
    const qs = p.toString();
    window.history.replaceState(null, '', `/techops${qs ? '?' + qs : ''}`);
  }, []);

  const loadList = useCallback(() => techopsApi.get('/change-requests').then(setRows).catch(() => setRows([])), []);
  const loadIncidents = useCallback(() => techopsApi.get('/incidents').then(setIncidents).catch(() => setIncidents([])), []);
  const loadOptions = useCallback(() => techopsApi.get('/options').then(setOptions).catch(() => {}), []);
  useEffect(() => {
    techopsApi.get('/me').then(setMe).catch(() => setMe({ techops_role: 'none', is_admin: false }));
    loadOptions();
    techopsApi.get('/members').then(setMembers).catch(() => {});
    techopsApi.get('/notifiable').then(setNotifiable).catch(() => {});
    loadList();
    loadIncidents();
    // Restore the view from the URL — this handles both the deep links from email
    // (?cr=… / ?incident=…) and a plain browser refresh (?view=…).
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get('view');
    const crId = params.get('cr');
    const incId = params.get('incident');
    if (crId) {
      techopsApi.get(`/change-requests/${crId}`)
        .then(r => { setCurrent(r); setView(wanted === 'form' ? 'form' : 'detail'); })
        .catch(() => {});
    } else if (incId) {
      techopsApi.get(`/incidents/${incId}`)
        .then(r => { setCurrentInc(r); setView(wanted === 'incident-form' ? 'incident-form' : 'incident-detail'); })
        .catch(() => {});
    } else if (wanted) {
      setView(wanted);
    }
  }, [loadList, loadIncidents, loadOptions]);

  // Keep the URL in step whenever the view (or the open record) changes.
  useEffect(() => {
    if (!me) return;
    const id = (view === 'detail' || view === 'form') ? (current && current.id)
      : (view === 'incident-detail' || view === 'incident-form') ? (currentInc && currentInc.id)
        : null;
    syncUrl(view, id);
  }, [view, current, currentInc, me, syncUrl]);

  if (!me) return <div style={{ padding: 40, color: 'var(--muted)' }}>Loading…</div>;

  const go = (v) => { setView(v); };

  return (
    <div style={{ display: 'flex', width: '100%', height: '100vh', flex: 1, minWidth: 0, background: 'var(--bg)' }}>
      {/* ── Sidebar ── */}
      <aside style={{
        width: sidebarOpen ? 232 : 0, flexShrink: 0, background: 'var(--surface)',
        borderRight: sidebarOpen ? '1px solid var(--border)' : 'none', overflow: 'hidden',
        transition: 'width .18s', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 15 }}>T</div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)', lineHeight: 1.1 }}>Technical Ops</div>
            <div style={{ fontSize: 9, color: 'var(--muted)', textTransform: 'capitalize', letterSpacing: '.3px' }}>{me.techops_role}</div>
          </div>
        </div>

        <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 10px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', padding: '4px 11px 6px' }}>Change Requests</div>
          <NavItem icon={<Ic.List />} label="All Requests" active={view === 'list' || view === 'detail'} onClick={() => go('list')} />
          <NavItem icon={<Ic.Plus />} label="New Request" active={view === 'form'} onClick={() => { setCurrent(null); go('form'); }} />
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', padding: '14px 11px 6px' }}>Incident Reports</div>
          <NavItem icon={<Ic.List />} label="All Incidents" active={view === 'incidents' || view === 'incident-detail'} onClick={() => go('incidents')} />
          <NavItem icon={<Ic.Plus />} label="New Incident" active={view === 'incident-form'} onClick={() => { setCurrentInc(null); go('incident-form'); }} />
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', padding: '14px 11px 6px' }}>Overview</div>
          <NavItem icon={<Ic.Dash />} label="Dashboard" active={view === 'dashboard'} onClick={() => go('dashboard')} />
          {me.is_admin && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', padding: '14px 11px 6px' }}>Administration</div>
              <NavItem icon={<Ic.Cog />} label="Settings" active={view === 'admin'} onClick={() => go('admin')} />
            </>
          )}
        </nav>

        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ width: 30, height: 30, borderRadius: '50%', background: BRAND, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {(user?.full_name || '?').charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.full_name}</div>
            <div style={{ fontSize: 10, color: 'var(--muted)' }}>@{user?.username}</div>
          </div>
          <button onClick={() => { logout(); navigate('/login'); }} title="Log out" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 2H3.5C2.7 2 2 2.7 2 3.5v9c0 .8.7 1.5 1.5 1.5H6M10.5 11l3-3-3-3M13 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </aside>

      {/* ── Main column: fixed topbar + scrollable content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <header style={{ height: 46, flexShrink: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 10 }}>
          <button onClick={() => setSidebarOpen(v => !v)} title="Toggle menu" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', padding: 4, display: 'flex' }}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M2 4.5h14M2 9h14M2 13.5h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </button>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Technical Operations</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => navigate('/hub')} title="App Hub — switch applications"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--muted2)', cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="4.5" height="4.5" rx="1" fill="currentColor" /><rect x="9.5" y="2" width="4.5" height="4.5" rx="1" fill="currentColor" /><rect x="2" y="9.5" width="4.5" height="4.5" rx="1" fill="currentColor" /><rect x="9.5" y="9.5" width="4.5" height="4.5" rx="1" fill="currentColor" /></svg>
            </button>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: 'rgba(37,99,235,0.10)', color: BRAND }}>TechOps</span>
          </div>
        </header>

        {/* Scrollable content area — fills the full width */}
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '22px 28px' }}>
          <ViewErrorBoundary viewKey={view}>
          {view === 'list' && <CRList rows={rows} onOpen={(r) => { setCurrent(r); setView('detail'); }} onNew={() => { setCurrent(null); setView('form'); }} />}
          {view === 'dashboard' && <Dashboard />}
          {view === 'form' && <CRForm options={options} members={members} existing={current} onClose={() => setView('list')} onSaved={(id) => { loadList(); techopsApi.get(`/change-requests/${id}`).then(r => { setCurrent(r); setView('detail'); }); }} />}
          {view === 'detail' && current && <CRDetail me={{ ...me, id: user?.id }} id={current.id} members={members} onBack={() => { loadList(); setView('list'); }} onEdit={(r) => { setCurrent(r); setView('form'); }} />}
          {view === 'incidents' && <IncidentList rows={incidents} onOpen={(r) => { setCurrentInc(r); setView('incident-detail'); }} onNew={() => { setCurrentInc(null); setView('incident-form'); }} />}
          {view === 'incident-form' && <IncidentForm options={options} members={members} notifiable={notifiable} existing={currentInc} onClose={() => setView('incidents')} onSaved={(id) => { loadIncidents(); techopsApi.get(`/incidents/${id}`).then(r => { setCurrentInc(r); setView('incident-detail'); }); }} />}
          {view === 'incident-detail' && currentInc && <IncidentDetail me={{ ...me, id: user?.id }} id={currentInc.id} members={members} notifiable={notifiable} onBack={() => { loadIncidents(); setView('incidents'); }} onEdit={(r) => { setCurrentInc(r); setView('incident-form'); }} />}
          {view === 'admin' && me.is_admin && <TechAdmin options={options} onOptionsChanged={loadOptions} />}
          </ViewErrorBoundary>
        </main>
      </div>
    </div>
  );
}

// If a view throws, show the error instead of a blank white page (a blank page gives
// nothing to act on; this shows what actually went wrong and lets you carry on).
class ViewErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('[TechOps view error]', error, info); }
  componentDidUpdate(prev) { if (prev.viewKey !== this.props.viewKey && this.state.error) this.setState({ error: null }); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ ...card, borderColor: '#DC2626' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#DC2626', marginBottom: 8 }}>Something went wrong on this page</div>
          <div style={{ fontSize: 13, color: 'var(--muted2)', marginBottom: 12 }}>
            {String(this.state.error && this.state.error.message ? this.state.error.message : this.state.error)}
          </div>
          <button onClick={() => this.setState({ error: null })} style={btn()}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

function PageHead({ title, sub, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18, gap: 12, flexWrap: 'wrap' }}>
      <div>
        <h1 style={{ fontSize: 21, fontWeight: 850, margin: 0, color: 'var(--text)' }}>{title}</h1>
        {sub && <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 3 }}>{sub}</div>}
      </div>
      {right && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{right}</div>}
    </div>
  );
}

function CRList({ rows, onOpen, onNew }) {
  const token = localStorage.getItem('flowup_token');
  const [q, setQ] = useState('');
  const filtered = rows.filter(r => !q || (r.cr_title || '').toLowerCase().includes(q.toLowerCase()) || (r.reference || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <PageHead title="Change Requests" sub={`${rows.length} request${rows.length === 1 ? '' : 's'} — visible to all Technical Operations members`}
        right={<>
          <a href={`/api/techops/export.xlsx?token=${token}`} style={{ ...btn('#0D9488'), textDecoration: 'none' }}>⬇ Excel Export</a>
          <button onClick={onNew} style={btn()}>+ New Change Request</button>
        </>} />
      <input style={{ ...inp, maxWidth: 320, marginBottom: 14 }} placeholder="Search by title or reference…" value={q} onChange={e => setQ(e.target.value)} />
      {!filtered.length ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--muted)', padding: 50 }}>
          {rows.length ? 'No requests match your search.' : 'No change requests yet. Create the first one.'}
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,160px) minmax(200px,1fr) 120px 140px minmax(140px,180px)', padding: '11px 16px', background: 'var(--surface2)', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5 }}>
            <div>Reference</div><div>CR Title</div><div>Category</div><div>Status</div><div>Requested By</div>
          </div>
          {filtered.map(r => (
            <div key={r.id} onClick={() => onOpen(r)}
              style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,160px) minmax(200px,1fr) 120px 140px minmax(140px,180px)', padding: '13px 16px', borderTop: '1px solid var(--border)', fontSize: 13, cursor: 'pointer', alignItems: 'center' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontWeight: 700, color: BRAND }}>{r.reference}</div>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 10 }}>{r.cr_title}</div>
              <div style={{ color: 'var(--muted2)' }}>{r.category || '—'}</div>
              <div><StatusPill status={r.status} /></div>
              <div style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.created_by_name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto', marginBottom: 14 }}>
      <label style={lbl}>{label}{required && <span style={{ color: '#DC2626' }}> *</span>}</label>
      {children}
    </div>
  );
}
function Sel({ value, onChange, opts }) {
  return (
    <select style={inp} value={value || ''} onChange={e => onChange(e.target.value)}>
      <option value="">—</option>
      {(opts || []).map(o => <option key={o.id} value={o.value}>{o.value}</option>)}
    </select>
  );
}
function Section({ n, t, children }) {
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: BRAND, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
        Section {n}: {t}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0 20px' }}>
        {children}
      </div>
    </div>
  );
}

function CRForm({ options, members, existing, onClose, onSaved }) {
  const [f, setF] = useState(() => {
    if (!existing) return { project_engineers: [], implemented_by: [], project_manager_ids: [], readiness: [], contractors_vendors: [], outage_impact: [], impacted_customers: [] };
    return { ...existing, project_engineers: parseArr(existing.project_engineers), implemented_by: parseArr(existing.implemented_by), project_manager_ids: parseArr(existing.project_manager_ids), readiness: parseArr(existing.readiness), contractors_vendors: parseArr(existing.contractors_vendors), outage_impact: parseArr(existing.outage_impact), impacted_customers: parseArr(existing.impacted_customers) };
  });
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const o = (field) => options[field] || [];
  const ta = { ...inp, minHeight: 80, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 };

  // When the activity end and the expected outage duration are both known, derive the
  // outage window automatically (outage starts at the activity end, and runs for the
  // duration). The user can still override either value afterwards.
  const applyOutageAuto = (nextActivityEnd, nextDurationMin) => {
    const endVal = nextActivityEnd !== undefined ? nextActivityEnd : f.activity_end;
    const durVal = nextDurationMin !== undefined ? nextDurationMin : f.outage_duration_min;
    const mins = parseInt(durVal, 10);
    if (!endVal || Number.isNaN(mins)) return {};
    const start = new Date(endVal);
    if (Number.isNaN(start.getTime())) return {};
    const end = new Date(start.getTime() + mins * 60000);
    return { outage_start: toLocal(start), outage_end: toLocal(end) };
  };

  const save = async () => {
    if (!String(f.cr_title || '').trim()) { setErr('CR Title is required.'); window.scrollTo(0, 0); return; }
    setSaving(true); setErr('');
    try {
      let id = existing?.id;
      if (id) await techopsApi.put(`/change-requests/${id}`, f);
      else { const r = await techopsApi.post('/change-requests', f); id = r.id; }
      if (files.length && id) {
        const fd = new FormData();
        files.forEach(file => fd.append('files', file));
        try { await techopsApi.post(`/change-requests/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch (e) {}
      }
      onSaved(id);
    } catch (e) { setErr(e.error || 'Failed to save'); window.scrollTo(0, 0); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 1200 }}>
      <PageHead title={`${existing ? 'Edit' : 'New'} Change Request`} sub={existing ? existing.reference : 'Fill in the details below'}
        right={<>
          <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
          <button onClick={save} disabled={saving} style={btn()}>{saving ? 'Saving…' : 'Save'}</button>
        </>} />
      {err && <div style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, fontWeight: 600 }}>{err}</div>}

      <Section n={1} t="CR Personal Information">
        <Field label="CR Title" required full><input style={inp} value={f.cr_title || ''} onChange={e => set('cr_title', e.target.value)} /></Field>
        <Field label="Project Engineer(s)"><UserPicker values={f.project_engineers} onChange={v => set('project_engineers', v)} members={members} /></Field>
        <Field label="Implemented By"><UserPicker values={f.implemented_by} onChange={v => set('implemented_by', v)} members={members} /></Field>
        <Field label="Implementer Role"><input style={inp} value={f.implementer_role || ''} onChange={e => set('implementer_role', e.target.value)} /></Field>
        <Field label="Requestor Role"><input style={inp} value={f.requestor_role || ''} onChange={e => set('requestor_role', e.target.value)} /></Field>
      </Section>

      <Section n={2} t="CR Details">
        <Field label="CR Main Description" full><textarea style={ta} value={f.main_description || ''} onChange={e => set('main_description', e.target.value)} /></Field>
        <Field label="CR Justification" full><textarea style={ta} value={f.justification || ''} onChange={e => set('justification', e.target.value)} /></Field>
        <Field label="CR Implementation Plan" full><ListTextarea style={{ ...ta, minHeight: 110 }} value={f.implementation_plan} onChange={v => set('implementation_plan', v)} /></Field>
        <Field label="CR Roll Back Plan" full><ListTextarea style={{ ...ta, minHeight: 90 }} value={f.rollback_plan} onChange={v => set('rollback_plan', v)} /></Field>
        <Field label="CR Category"><Sel value={f.category} onChange={v => set('category', v)} opts={o('category')} /></Field>
        <Field label="CR Purpose"><input style={inp} value={f.purpose || ''} onChange={e => set('purpose', e.target.value)} /></Field>
        <Field label="CR Readiness"><MultiPicker values={f.readiness} onChange={v => set('readiness', v)} options={o('readiness')} placeholder="Select readiness…" /></Field>
        <Field label="Contractors & Vendors"><MultiPicker values={f.contractors_vendors} onChange={v => set('contractors_vendors', v)} options={o('contractors_vendors')} placeholder="Select contractors/vendors…" /></Field>
        <Field label="Activity Start (Plan) — date & time"><input type="datetime-local" style={inp} value={f.activity_start ? toLocal(f.activity_start) : ''} onChange={e => set('activity_start', e.target.value)} /></Field>
        <Field label="Activity End — date & time">
          <input type="datetime-local" style={inp} value={f.activity_end ? toLocal(f.activity_end) : ''}
            onChange={e => setF(prev => ({ ...prev, activity_end: e.target.value, ...applyOutageAuto(e.target.value, undefined) }))} />
        </Field>
      </Section>

      <Section n={3} t="CR Outage Details">
        <Field label="Maintenance Window Type"><Sel value={f.mw_type} onChange={v => set('mw_type', v)} opts={o('mw_type')} /></Field>
        <Field label="Maintenance Window ID">
          <input style={{ ...inp, background: 'var(--surface2)', color: 'var(--muted2)' }} value={f.mw_id || '— assigned automatically on save —'} readOnly />
        </Field>
        <Field label="Outage Type"><Sel value={f.outage_type} onChange={v => set('outage_type', v)} opts={o('outage_type')} /></Field>
        <Field label="Expected Outage Duration (minutes)">
          <input type="number" min="0" style={inp} value={f.outage_duration_min ?? ''}
            onChange={e => setF(prev => ({ ...prev, outage_duration_min: e.target.value, ...applyOutageAuto(undefined, e.target.value) }))} />
        </Field>
        <Field label="Outage Start — date & time"><input type="datetime-local" style={inp} value={f.outage_start ? toLocal(f.outage_start) : ''} onChange={e => set('outage_start', e.target.value)} /></Field>
        <Field label="Outage End — date & time"><input type="datetime-local" style={inp} value={f.outage_end ? toLocal(f.outage_end) : ''} onChange={e => set('outage_end', e.target.value)} /></Field>
        <Field label="Outage Service Main Impact"><MultiPicker values={f.outage_impact} onChange={v => set('outage_impact', v)} options={o('outage_impact')} placeholder="Select impacted services…" /></Field>
        <Field label="Impacted Customers"><MultiPicker values={f.impacted_customers} onChange={v => set('impacted_customers', v)} options={o('impacted_customers')} placeholder="Select customers…" /></Field>
        <Field label="Business impact for SLAs or Corporate" full><textarea style={ta} value={f.business_impact || ''} onChange={e => set('business_impact', e.target.value)} /></Field>
      </Section>

      <Section n={4} t="CR Monitoring & Operation">
        <Field label="Result Observation Period After CR"><Sel value={f.result_obs_period} onChange={v => set('result_obs_period', v)} opts={o('result_obs_period')} /></Field>
        <Field label="CR Status"><Sel value={f.cr_status} onChange={v => set('cr_status', v)} opts={o('cr_status')} /></Field>
        <Field label="Recommendation & Risk" full><textarea style={ta} value={f.recommendation || ''} onChange={e => set('recommendation', e.target.value)} /></Field>
      </Section>

      <Section n={5} t="Approval & Evidence">
        <Field label="Project Manager(s) who will approve" required full>
          <UserPicker values={f.project_manager_ids} onChange={v => set('project_manager_ids', v)} members={members} placeholder="Select approver(s)…" />
        </Field>
        <Field label="Attach evidence files" full>
          <input type="file" multiple onChange={e => { setFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; }} style={{ fontSize: 12 }} />
          {files.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {files.map((x, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--surface2)', borderRadius: 7, padding: '6px 11px', fontSize: 12 }}>
                  <span>📄 {x.name}</span>
                  <button onClick={() => setFiles(files.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', color: '#DC2626', cursor: 'pointer', fontWeight: 700, fontSize: 15 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </Field>
      </Section>

      <div style={{ display: 'flex', gap: 10, marginBottom: 30 }}>
        <button onClick={save} disabled={saving} style={btn()}>{saving ? 'Saving…' : 'Save Change Request'}</button>
        <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
      </div>
    </div>
  );
}

function CRDetail({ me, id, members, onBack, onEdit }) {
  const [r, setR] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHist, setShowHist] = useState(false);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    techopsApi.get(`/change-requests/${id}`).then(setR).catch(() => {});
    techopsApi.get(`/change-requests/${id}/history`).then(setHistory).catch(() => setHistory([]));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (!r) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;

  const act = async (fn) => { setBusy(true); setErr(''); try { await fn(); load(); } catch (e) { setErr(e.error || 'Failed'); } setBusy(false); };
  const nameOf = id2 => (members.find(m => m.id === id2) || {}).full_name || '';
  const pmIds = parseArr(r.project_manager_ids);
  const isOwner = r.created_by === me.id;
  const isManager = pmIds.includes(me.id);
  const canEdit = (isOwner || me.is_admin) && r.status === 'draft';
  // Owners delete drafts only; ADMINS can delete at any status (kept in the history log).
  const canDelete = me.is_admin || (isOwner && r.status === 'draft');
  const token = localStorage.getItem('flowup_token');

  const facts = [
    ['Category', r.category], ['CR Status', r.cr_status],
    ['Project Engineer(s)', parseArr(r.project_engineers).map(nameOf).join(', ')],
    ['Implemented By', parseArr(r.implemented_by).map(nameOf).join(', ')],
    ['Implementer Role', r.implementer_role], ['Requestor Role', r.requestor_role],
    ['Purpose', r.purpose],
    ['Readiness', parseArr(r.readiness).join(', ')],
    ['Contractors/Vendors', parseArr(r.contractors_vendors).join(', ')],
    ['Activity Start', r.activity_start ? new Date(r.activity_start).toLocaleString('en-GB') : ''],
    ['Activity End', r.activity_end ? new Date(r.activity_end).toLocaleString('en-GB') : ''],
    ['MW Type', r.mw_type], ['MW ID', r.mw_id],
    ['Outage Type', r.outage_type],
    ['Outage Start', r.outage_start ? new Date(r.outage_start).toLocaleString('en-GB') : ''],
    ['Outage End', r.outage_end ? new Date(r.outage_end).toLocaleString('en-GB') : ''],
    ['Outage Impact', parseArr(r.outage_impact).join(', ')],
    ['Impacted Customers', parseArr(r.impacted_customers).join(', ')],
    ['Outage Duration (min)', r.outage_duration_min],
    ['Result Obs. Period', r.result_obs_period],
    ['Approver(s)', pmIds.map(nameOf).join(', ')],
  ];

  // Who has signed: requester signs on submit; an approver signs only when they approve.
  let approvals = {}; try { approvals = JSON.parse(r.approvals || '{}'); } catch (e) {}

  return (
    <div style={{ maxWidth: 1200 }}>
      <button onClick={onBack} style={{ ...btn('transparent', BRAND), padding: '4px 0', marginBottom: 10 }}>← Back to all requests</button>
      <PageHead title={r.cr_title} sub={`${r.reference}${r.mw_id ? ' · ' + r.mw_id : ''}`}
        right={<>
          <a href={`/api/techops/change-requests/${r.id}/pdf?token=${token}`} style={{ ...btn('#0D9488'), textDecoration: 'none' }}>⬇ PDF</a>
          <a href={`/api/techops/change-requests/${r.id}/docx?token=${token}`} style={{ ...btn('#1F3864'), textDecoration: 'none' }}>⬇ Word</a>
          {canEdit && <button onClick={() => onEdit(r)} style={btn('var(--surface2)', 'var(--text)')}>✎ Edit</button>}
          <StatusPill status={r.status} />
        </>} />
      {err && <div style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{err}</div>}

      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '4px 24px', fontSize: 13 }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--muted)', minWidth: 140 }}>{k}:</span>
              <b style={{ flex: 1, wordBreak: 'break-word' }}>{(v === 0 ? '0' : v) || '—'}</b>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...card, marginBottom: 16 }}>
        <DetailBlock label="Main Description" v={r.main_description} />
        <DetailBlock label="Justification" v={r.justification} />
        <DetailBlock label="Implementation Plan" v={r.implementation_plan} />
        <DetailBlock label="Roll Back Plan" v={r.rollback_plan} />
        <DetailBlock label="Business Impact" v={r.business_impact} />
        <DetailBlock label="Recommendation & Risk" v={r.recommendation} />
      </div>

      {/* Signatures state — who has signed and when */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 10 }}>Signatures</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px,1fr))', gap: 12 }}>
          <SigCard title="Requested By" name={r.created_by_name} at={r.requester_signed_at} />
          {pmIds.map(pid => {
            const a = approvals[pid];
            return <SigCard key={pid} title="Approved By" name={nameOf(pid)} at={a && !a.rejected ? a.at : null} rejected={a && a.rejected} comment={a && a.comment} />;
          })}
        </div>
      </div>

      {r.attachments && r.attachments.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Evidence Files</div>
          {r.attachments.map(a => (
            <a key={a.id} href={`/api/techops/attachments/${a.id}/download?token=${token}`} style={{ display: 'block', fontSize: 13, color: BRAND, padding: '4px 0', textDecoration: 'none' }}>📎 {a.file_name}</a>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {(isOwner || me.is_admin) && r.status === 'draft' && <button onClick={() => act(() => techopsApi.post(`/change-requests/${r.id}/submit`, {}))} disabled={busy} style={btn()}>Submit for Approval</button>}
        {canDelete && (
          <button onClick={() => {
            const warn = r.status === 'draft'
              ? 'Delete this change request?'
              : `This request is ${r.status.replace('_', ' ')}. Deleting it is permanent — the deletion will be recorded in the history log. Continue?`;
            if (window.confirm(warn)) act(() => techopsApi.delete(`/change-requests/${r.id}`).then(onBack));
          }} style={btn('rgba(220,38,38,0.1)', '#DC2626')}>Delete</button>
        )}
      </div>

      {r.status === 'pending_approval' && (isManager || me.is_admin) && (
        <div style={{ ...card, marginBottom: 16, borderColor: BRAND }}>
          <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10 }}>Approval decision — you are an approver</div>
          <textarea style={{ ...inp, minHeight: 50, marginBottom: 10 }} value={comment} onChange={e => setComment(e.target.value)} placeholder="Comment (optional)…" />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => act(() => techopsApi.post(`/change-requests/${r.id}/decision`, { decision: 'approve', comment }))} disabled={busy} style={btn('#16A34A')}>Approve &amp; Sign</button>
            <button onClick={() => act(() => techopsApi.post(`/change-requests/${r.id}/decision`, { decision: 'reject', comment }))} disabled={busy} style={btn('rgba(220,38,38,0.1)', '#DC2626')}>Reject</button>
          </div>
          {pmIds.length > 1 && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 8 }}>This request needs approval from all {pmIds.length} approvers before it is fully approved.</div>}
        </div>
      )}

      {/* Audit history — opens in a window */}
      <div style={{ marginBottom: 30 }}>
        <button onClick={() => setShowHist(true)} style={{ ...btn('var(--surface2)', 'var(--text)'), border: '1px solid var(--border)' }}>
          🕘 View History{history.length ? ` (${history.length})` : ''}
        </button>
      </div>
      {showHist && (
        <Modal title={`History — ${r.reference}`} onClose={() => setShowHist(false)}>
          <HistoryTimeline history={history} />
        </Modal>
      )}
    </div>
  );
}

function SigCard({ title, name, at, rejected, comment }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 9, padding: 12, background: 'var(--surface2)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 5 }}>{title}</div>
      <div style={{ fontSize: 13, fontWeight: 700 }}>{name || '—'}</div>
      {at ? (
        <>
          <div style={{ fontSize: 12, color: '#16A34A', fontWeight: 700, marginTop: 4 }}>✓ Signed</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{new Date(at).toLocaleString('en-GB')}</div>
        </>
      ) : rejected ? (
        <>
          <div style={{ fontSize: 12, color: '#DC2626', fontWeight: 700, marginTop: 4 }}>✕ Rejected — not signed</div>
          {comment && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{comment}</div>}
        </>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Awaiting signature</div>
      )}
    </div>
  );
}

// A modal window — used for the History view.
function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 14, width: '100%', maxWidth: 640,
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--muted)', lineHeight: 1, padding: 0 }}>×</button>
        </div>
        <div style={{ padding: 18, overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

// The history timeline body, shared by change requests and incidents.
function HistoryTimeline({ history }) {
  if (!history || !history.length) return <div style={{ fontSize: 13, color: 'var(--muted)' }}>No history yet.</div>;
  const colorOf = a => (a === 'rejected' || a === 'deleted') ? '#DC2626'
    : (a === 'approved' || a === 'notified') ? '#16A34A' : BRAND;
  return (
    <div>
      {history.map((h, i) => (
        <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < history.length - 1 ? '1px solid var(--border)' : 'none' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: colorOf(h.action), marginTop: 5, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'capitalize' }}>{h.action}</div>
            {h.detail && <div style={{ fontSize: 12.5, color: 'var(--muted2)', marginTop: 2 }}>{h.detail}</div>}
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 3 }}>
              {h.actor_name || 'System'} · {new Date(h.created_at).toLocaleString('en-GB')}
              {h.from_status && h.to_status && h.from_status !== h.to_status && ` · ${h.from_status} → ${h.to_status}`}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DetailBlock({ label, v, always }) {
  // `always` keeps the section visible even when it is empty (so the reader sees the
  // heading and knows nothing was recorded, rather than the section vanishing).
  if (!v && !always) return null;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 4 }}>{label}</div>
      {v ? <RichText text={v} />
        : <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>—</div>}
    </div>
  );
}

function Dashboard() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState('');
  const [d, setD] = useState(null);
  const token = localStorage.getItem('flowup_token');
  useEffect(() => {
    const q = new URLSearchParams({ year }); if (month) q.set('month', month);
    techopsApi.get(`/dashboard?${q.toString()}`).then(setD).catch(() => setD(null));
  }, [year, month]);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const stat = (label, val, color) => (
    <div style={{ ...card, flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: 30, fontWeight: 850, color }}>{val ?? 0}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600, marginTop: 2 }}>{label}</div>
    </div>
  );
  const maxMonthly = d ? Math.max(1, ...(d.monthly || []).map(m => m.c)) : 1;
  return (
    <div style={{ maxWidth: 1200 }}>
      <PageHead title="Dashboard" sub="Change request and incident activity"
        right={<a href={`/api/techops/export.xlsx?token=${token}&year=${year}${month ? '&month=' + month : ''}`} style={{ ...btn('#0D9488'), textDecoration: 'none' }}>⬇ Excel Export</a>} />
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <select style={{ ...inp, width: 'auto' }} value={year} onChange={e => setYear(e.target.value)}>
          {[0, 1, 2, 3].map(i => now.getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select style={{ ...inp, width: 'auto' }} value={month} onChange={e => setMonth(e.target.value)}>
          <option value="">All months</option>
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
      </div>
      {!d ? <div style={{ color: 'var(--muted)' }}>Loading…</div> : (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
            {stat('Total Change Requests', d.total, BRAND)}
            {stat('Pending', d.pending, '#B45309')}
            {stat('Completed', d.completed, '#16A34A')}
            {stat('Rejected', d.rejected, '#DC2626')}
          </div>
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 14 }}>Monthly Change Requests — {year}</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160 }}>
              {MONTHS.map((mn, i) => {
                const c = (d.monthly || []).find(x => x.m === i + 1)?.c || 0;
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, height: '100%', justifyContent: 'flex-end' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted2)' }}>{c || ''}</div>
                    <div style={{ width: '100%', maxWidth: 46, height: `${(c / maxMonthly) * 100}%`, minHeight: c ? 5 : 0, background: BRAND, borderRadius: '5px 5px 0 0' }} />
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{mn}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 26 }}>
            <BreakdownCard title="By Category" data={d.by_category} keyName="category" />
            <BreakdownCard title="By Outage Type" data={d.by_outage_type} keyName="t" />
          </div>

          {/* ── Incident Reports ── */}
          <div style={{ fontSize: 16, fontWeight: 850, margin: '4px 0 14px', color: 'var(--text)' }}>Incident Reports</div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
            {stat('Total Incidents', d.inc_total, BRAND)}
            {stat('Notified', d.inc_published, '#16A34A')}
            {stat('Draft', d.inc_draft, '#6B7280')}
            {stat('Total Outage (min)', d.inc_outage_minutes, '#DC2626')}
          </div>
          <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 14 }}>Monthly Incidents — {year}</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 160 }}>
              {MONTHS.map((mn, i) => {
                const c = (d.inc_monthly || []).find(x => x.m === i + 1)?.c || 0;
                const maxInc = Math.max(1, ...((d.inc_monthly || []).map(m => m.c)));
                return (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, height: '100%', justifyContent: 'flex-end' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted2)' }}>{c || ''}</div>
                    <div style={{ width: '100%', maxWidth: 46, height: `${(c / maxInc) * 100}%`, minHeight: c ? 5 : 0, background: '#DC2626', borderRadius: '5px 5px 0 0' }} />
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{mn}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 30 }}>
            <BreakdownCard title="Incidents by Network Element" data={d.inc_by_element} keyName="t" />
            <BreakdownCard title="Incidents by Impact" data={d.inc_by_impact} keyName="t" />
          </div>
        </>
      )}
    </div>
  );
}
function BreakdownCard({ title, data, keyName }) {
  const total = (data || []).reduce((s, r) => s + r.c, 0) || 1;
  return (
    <div style={{ ...card }}>
      <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 12 }}>{title}</div>
      {(data || []).map((row, i) => (
        <div key={i} style={{ marginBottom: 9 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
            <span>{row[keyName] || '—'}</span><b>{row.c}</b>
          </div>
          <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ width: `${(row.c / total) * 100}%`, height: '100%', background: BRAND, borderRadius: 99 }} />
          </div>
        </div>
      ))}
      {(!data || !data.length) && <div style={{ color: 'var(--muted)', fontSize: 13 }}>No data</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  INCIDENT REPORTS — a notification, not an approval.
// ═══════════════════════════════════════════════════════════════════════════
function IncStatusPill({ status }) {
  const s = INC_STATUS[status] || INC_STATUS.draft;
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99, color: s.color, background: s.bg, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

function IncidentList({ rows, onOpen, onNew }) {
  const [q, setQ] = useState('');
  const filtered = rows.filter(r => !q || (r.incident_title || '').toLowerCase().includes(q.toLowerCase()) || (r.reference || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <PageHead title="Incident Reports" sub={`${rows.length} report${rows.length === 1 ? '' : 's'} — visible to all Technical Operations members`}
        right={<button onClick={onNew} style={btn()}>+ New Incident Report</button>} />
      <input style={{ ...inp, maxWidth: 320, marginBottom: 14 }} placeholder="Search by title or reference…" value={q} onChange={e => setQ(e.target.value)} />
      {!filtered.length ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--muted)', padding: 50 }}>
          {rows.length ? 'No reports match your search.' : 'No incident reports yet.'}
        </div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,160px) minmax(200px,1fr) 170px 120px minmax(140px,180px)', padding: '11px 16px', background: 'var(--surface2)', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5 }}>
            <div>Reference</div><div>Incident Title</div><div>Started</div><div>Status</div><div>Reported By</div>
          </div>
          {filtered.map(r => (
            <div key={r.id} onClick={() => onOpen(r)}
              style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,160px) minmax(200px,1fr) 170px 120px minmax(140px,180px)', padding: '13px 16px', borderTop: '1px solid var(--border)', fontSize: 13, cursor: 'pointer', alignItems: 'center' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontWeight: 700, color: BRAND }}>{r.reference}</div>
              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 10 }}>{r.incident_title}</div>
              <div style={{ color: 'var(--muted2)', fontSize: 12 }}>{r.incident_start ? new Date(r.incident_start).toLocaleString('en-GB') : '—'}</div>
              <div><IncStatusPill status={r.status} /></div>
              <div style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.created_by_name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IncidentForm({ options, members, notifiable, existing, onClose, onSaved }) {
  const [f, setF] = useState(() => {
    if (!existing) return { network_elements: [], impact: [], notify_user_ids: [] };
    return { ...existing, network_elements: parseArr(existing.network_elements), impact: parseArr(existing.impact), notify_user_ids: parseArr(existing.notify_user_ids) };
  });
  // Appendix: existing (already saved) images + new ones waiting to upload.
  // Existing: { id, caption, existing: true }   New: { file, caption }
  const [pics, setPics] = useState(() => (existing?.appendix || []).map(a => ({ id: a.id, caption: a.caption || '', existing: true })));
  const [removedIds, setRemovedIds] = useState([]);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const o = (field) => options[field] || [];
  const ta = { ...inp, minHeight: 80, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 };

  // Derive the outage duration from start/end so it matches the times.
  const applyDuration = (start, end) => {
    const s = start !== undefined ? start : f.incident_start;
    const e = end !== undefined ? end : f.incident_end;
    if (!s || !e) return {};
    const ms = new Date(e) - new Date(s);
    if (Number.isNaN(ms) || ms < 0) return {};
    const mins = Math.round(ms / 60000);
    return { outage_duration: `${mins} minute${mins === 1 ? '' : 's'}` };
  };

  const addPics = (fileList) => {
    // Appendix accepts pictures only.
    const files = Array.from(fileList).filter(f => (f.type || '').startsWith('image/'));
    const rejected = Array.from(fileList).length - files.length;
    if (rejected > 0) setErr(`${rejected} file${rejected === 1 ? ' was' : 's were'} ignored — the Appendix accepts pictures only.`);
    setPics(prev => [...prev, ...files.map(file => ({ file, caption: '' }))]);
  };
  const movePic = (i, dir) => {
    setPics(prev => {
      const arr = [...prev];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };
  const removePic = (i) => {
    setPics(prev => {
      const p = prev[i];
      if (p && p.existing && p.id) setRemovedIds(r => [...r, p.id]);
      return prev.filter((_, j) => j !== i);
    });
  };

  // Pasting a picture into the text needs the incident to exist first (the image is
  // attached to it). If this is a brand-new report, save a draft silently, then upload.
  const [incId, setIncId] = useState(existing?.id || null);
  const pasteImage = async (file) => {
    let id = incId;
    if (!id) {
      if (!String(f.incident_title || '').trim()) {
        setErr('Enter the Incident Title first, then you can paste pictures into the text.');
        window.scrollTo(0, 0);
        return null;
      }
      const created = await techopsApi.post('/incidents', f);
      id = created.id;
      setIncId(id);
    }
    const fd = new FormData();
    fd.append('file', file);
    const res = await techopsApi.post(`/incidents/${id}/inline-image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return res.marker;
  };

  const save = async () => {
    if (!String(f.incident_title || '').trim()) { setErr('Incident Title is required.'); window.scrollTo(0, 0); return; }
    setSaving(true); setErr('');
    try {
      let id = existing?.id || incId;
      if (id) await techopsApi.put(`/incidents/${id}`, f);
      else { const r = await techopsApi.post('/incidents', f); id = r.id; setIncId(id); }

      // Delete any existing appendix images the user removed.
      for (const aid of removedIds) {
        try { await techopsApi.delete(`/appendix/${aid}`); } catch (e) {}
      }
      // Update caption + order of the existing images that remain.
      for (let i = 0; i < pics.length; i++) {
        const p = pics[i];
        if (p.existing && p.id) {
          try { await techopsApi.put(`/appendix/${p.id}`, { caption: p.caption, sort_order: i }); } catch (e) {}
        }
      }
      // Upload the new images, preserving their position in the list.
      const fresh = pics.map((p, i) => ({ ...p, idx: i })).filter(p => !p.existing && p.file);
      if (fresh.length && id) {
        const fd = new FormData();
        fresh.forEach(p => {
          fd.append('files', p.file);
          fd.append('captions', p.caption || p.file.name);
          fd.append('orders', String(p.idx));
        });
        try { await techopsApi.post(`/incidents/${id}/appendix`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch (e) {}
      }
      setRemovedIds([]);
      onSaved(id);
    } catch (e) { setErr(e.error || 'Failed to save'); window.scrollTo(0, 0); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 1200 }}>
      <PageHead title={`${existing ? 'Edit' : 'New'} Incident Report`} sub={existing ? existing.reference : 'Record the incident and choose who to notify'}
        right={<>
          <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
          <button onClick={save} disabled={saving} style={btn()}>{saving ? 'Saving…' : 'Save'}</button>
        </>} />
      {err && <div style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, fontWeight: 600 }}>{err}</div>}

      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: BRAND, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>Incident Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0 20px' }}>
          <Field label="Incident Title" required full><input style={inp} value={f.incident_title || ''} onChange={e => set('incident_title', e.target.value)} /></Field>
          <Field label="Incident Start Time">
            <input type="datetime-local" style={inp} value={f.incident_start ? toLocal(f.incident_start) : ''}
              onChange={e => setF(prev => ({ ...prev, incident_start: e.target.value, ...applyDuration(e.target.value, undefined) }))} />
          </Field>
          <Field label="Incident End Time">
            <input type="datetime-local" style={inp} value={f.incident_end ? toLocal(f.incident_end) : ''}
              onChange={e => setF(prev => ({ ...prev, incident_end: e.target.value, ...applyDuration(undefined, e.target.value) }))} />
          </Field>
          <Field label="Outage Duration"><input style={inp} value={f.outage_duration || ''} onChange={e => set('outage_duration', e.target.value)} placeholder="e.g. 30 minutes" /></Field>
          <Field label="Network Elements"><MultiPicker values={f.network_elements} onChange={v => set('network_elements', v)} options={o('network_elements')} placeholder="Select network elements…" /></Field>
          <Field label="Impact"><MultiPicker values={f.impact} onChange={v => set('impact', v)} options={o('incident_impact')} placeholder="Select impact…" /></Field>
          <Field label="Incident Description" full>
            <ListTextarea style={ta} value={f.description} onChange={v => set('description', v)} onPasteImage={pasteImage} />
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>You can paste a picture directly into the text — it is placed on its own line.</div>
          </Field>
          <Field label="Actions Taken to resolve the Fault" full>
            <ListTextarea style={{ ...ta, minHeight: 160 }} value={f.actions_taken} onChange={v => set('actions_taken', v)} onPasteImage={pasteImage} />
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 4 }}>You can paste a picture directly into the text — it is placed on its own line.</div>
          </Field>
          <Field label="Root Cause Analysis" full><ListTextarea style={{ ...ta, minHeight: 90 }} value={f.root_cause} onChange={v => set('root_cause', v)} onPasteImage={pasteImage} /></Field>
        </div>
      </div>

      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: BRAND, marginBottom: 6, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>Appendix</div>
        <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 8 }}>
          Pictures only. Give each one a name and arrange them — they appear in this order in the system,
          the PDF and the Word document.
        </p>
        <input type="file" accept="image/*" multiple onChange={e => { addPics(e.target.files); e.target.value = ''; }} style={{ fontSize: 12, marginBottom: 10 }} />
        {pics.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pics.map((p, i) => (
              <div key={p.id || `new-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--surface2)', borderRadius: 9, padding: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', width: 22 }}>{i + 1}.</span>
                <img
                  src={p.existing
                    ? `/api/techops/appendix/${p.id}/file?token=${localStorage.getItem('flowup_token')}`
                    : (p.file ? URL.createObjectURL(p.file) : '')}
                  alt="" style={{ width: 52, height: 40, objectFit: 'cover', borderRadius: 5, flexShrink: 0, border: '1px solid var(--border)' }} />
                <input style={{ ...inp, flex: 1 }} value={p.caption} placeholder="Name this image (e.g. During the incident)"
                  onChange={e => setPics(prev => prev.map((x, j) => j === i ? { ...x, caption: e.target.value } : x))} />
                <button type="button" onClick={() => movePic(i, -1)} disabled={i === 0} style={{ ...btn('var(--surface)', 'var(--text)'), padding: '5px 9px', border: '1px solid var(--border)', opacity: i === 0 ? .4 : 1 }}>↑</button>
                <button type="button" onClick={() => movePic(i, 1)} disabled={i === pics.length - 1} style={{ ...btn('var(--surface)', 'var(--text)'), padding: '5px 9px', border: '1px solid var(--border)', opacity: i === pics.length - 1 ? .4 : 1 }}>↓</button>
                <button type="button" onClick={() => removePic(i)} style={{ ...btn('rgba(220,38,38,0.1)', '#DC2626'), padding: '5px 10px' }}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: BRAND, marginBottom: 6, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>Notify</div>
        <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 8 }}>
          These people are emailed the report — the key details plus a direct link to it. No approval is required.
        </p>
        <UserPicker values={f.notify_user_ids} onChange={v => set('notify_user_ids', v)} members={(notifiable && notifiable.length) ? notifiable : members} placeholder="Select people to notify…" />
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 30 }}>
        <button onClick={save} disabled={saving} style={btn()}>{saving ? 'Saving…' : 'Save Incident Report'}</button>
        <button onClick={onClose} style={btn('var(--surface2)', 'var(--text)')}>Cancel</button>
      </div>
    </div>
  );
}

function IncidentDetail({ me, id, members, notifiable, onBack, onEdit }) {
  const [r, setR] = useState(null);
  const [showHist, setShowHist] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(() => techopsApi.get(`/incidents/${id}`).then(setR).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);
  if (!r) return <div style={{ color: 'var(--muted)' }}>Loading…</div>;

  const act = async (fn) => { setBusy(true); setErr(''); try { await fn(); load(); } catch (e) { setErr(e.error || 'Failed'); } setBusy(false); };
  const people = (notifiable && notifiable.length) ? notifiable : members;
  const nameOf = id2 => (people.find(m => m.id === id2) || {}).full_name || '';
  const isOwner = r.created_by === me.id;
  const canEdit = isOwner || me.is_admin;
  const canDelete = me.is_admin || (isOwner && r.status === 'draft');
  const token = localStorage.getItem('flowup_token');
  const notifyIds = parseArr(r.notify_user_ids);

  const notify = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const res = await techopsApi.post(`/incidents/${r.id}/notify`, {});
      let m = `Notified ${res.notified} recipient${res.notified === 1 ? '' : 's'}.`;
      if (res.failed && res.failed.length) m += ` Could not send to: ${res.failed.join(', ')}.`;
      if (res.noEmail && res.noEmail.length) m += ` No email address on file for: ${res.noEmail.join(', ')}.`;
      setMsg(m);
      load();
    } catch (e) { setErr(e.error || 'Failed to notify'); }
    setBusy(false);
  };

  const facts = [
    ['Incident Start', r.incident_start ? new Date(r.incident_start).toLocaleString('en-GB') : ''],
    ['Incident End', r.incident_end ? new Date(r.incident_end).toLocaleString('en-GB') : ''],
    ['Outage Duration', r.outage_duration],
    ['Network Elements', parseArr(r.network_elements).join(', ')],
    ['Impact', parseArr(r.impact).join(', ')],
    ['Notify', notifyIds.map(nameOf).join(', ')],
  ];

  return (
    <div style={{ maxWidth: 1200 }}>
      <button onClick={onBack} style={{ ...btn('transparent', BRAND), padding: '4px 0', marginBottom: 10 }}>← Back to all incidents</button>
      <PageHead title={r.incident_title} sub={r.reference}
        right={<>
          <a href={`/api/techops/incidents/${r.id}/pdf?token=${token}`} style={{ ...btn('#0D9488'), textDecoration: 'none' }}>⬇ PDF</a>
          <a href={`/api/techops/incidents/${r.id}/docx?token=${token}`} style={{ ...btn('#1F3864'), textDecoration: 'none' }}>⬇ Word</a>
          {canEdit && <button onClick={() => onEdit(r)} style={btn('var(--surface2)', 'var(--text)')}>✎ Edit</button>}
          <IncStatusPill status={r.status} />
        </>} />
      {err && <div style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{err}</div>}
      {msg && <div style={{ background: 'rgba(22,163,74,0.1)', color: '#16A34A', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14, fontWeight: 600 }}>{msg}</div>}

      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '4px 24px', fontSize: 13 }}>
          {facts.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 8, padding: '5px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--muted)', minWidth: 140 }}>{k}:</span>
              <b style={{ flex: 1, wordBreak: 'break-word' }}>{v || '—'}</b>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...card, marginBottom: 16 }}>
        <DetailBlock label="Incident Description" v={r.description} always incidentId={r.id} />
        <DetailBlock label="Actions Taken to resolve the Fault" v={r.actions_taken} always incidentId={r.id} />
        <DetailBlock label="Root Cause Analysis" v={r.root_cause} incidentId={r.id} />
      </div>

      {r.appendix && r.appendix.length > 0 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 12 }}>Appendix</div>
          {r.appendix.map((a, i) => (
            <div key={a.id} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{i + 1}. {a.caption}</div>
              <img src={`/api/techops/appendix/${a.id}/file?token=${token}`} alt={a.caption}
                style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid var(--border)' }} />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {canEdit && (
          <button
            onClick={() => {
              if (!notifyIds.length) {
                setErr('No one is selected yet. Click Edit, choose the people to notify under "Notify", save, then send.');
                return;
              }
              notify();
            }}
            disabled={busy}
            style={btn(busy ? '#9CA3AF' : '#16A34A')}>
            {busy ? 'Sending…' : (r.status === 'published' ? '✉ Re-send notification' : '✉ Notify selected people')}
          </button>
        )}
        {canDelete && (
          <button onClick={() => {
            const warn = r.status === 'draft' ? 'Delete this incident report?'
              : 'This report has already been sent. Deleting it is permanent — the deletion is recorded in the history log. Continue?';
            if (window.confirm(warn)) act(() => techopsApi.delete(`/incidents/${r.id}`).then(onBack));
          }} style={btn('rgba(220,38,38,0.1)', '#DC2626')}>Delete</button>
        )}
      </div>
      {!notifyIds.length && canEdit && (
        <div style={{ fontSize: 12.5, color: '#B45309', marginBottom: 16 }}>Choose at least one person to notify (edit the report) before sending.</div>
      )}

      <div style={{ marginBottom: 30 }}>
        <button onClick={() => setShowHist(true)} style={{ ...btn('var(--surface2)', 'var(--text)'), border: '1px solid var(--border)' }}>
          🕘 View History{(r.history || []).length ? ` (${r.history.length})` : ''}
        </button>
      </div>
      {showHist && (
        <Modal title={`History — ${r.reference}`} onClose={() => setShowHist(false)}>
          <HistoryTimeline history={r.history} />
        </Modal>
      )}
    </div>
  );
}

function TechAdmin({ options, onOptionsChanged }) {
  const [tab, setTab] = useState('access');
  const [access, setAccess] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [newOpt, setNewOpt] = useState({});
  const [settings, setSettings] = useState({});
  const [savedMsg, setSavedMsg] = useState('');
  useEffect(() => { if (tab === 'access' && access === null) techopsApi.get('/access').then(setAccess).catch(() => setAccess([])); }, [tab, access]);
  useEffect(() => { techopsApi.get('/settings').then(setSettings).catch(() => {}); }, []);

  const setRole = async (uid, role) => {
    setSavingId(uid);
    try { await techopsApi.put(`/access/${uid}`, { techops_role: role }); setAccess(l => l.map(u => u.id === uid ? { ...u, techops_role: role } : u)); }
    catch (e) { alert(e?.error || 'Failed'); } finally { setSavingId(null); }
  };
  const addOpt = async (field) => {
    const value = (newOpt[field] || '').trim(); if (!value) return;
    try { await techopsApi.post('/options', { field, value }); setNewOpt(p => ({ ...p, [field]: '' })); onOptionsChanged(); } catch (e) { alert(e?.error || 'Failed'); }
  };
  const delOpt = async (id) => { try { await techopsApi.delete(`/options/${id}`); onOptionsChanged(); } catch (e) {} };
  const saveSettings = async () => {
    try { await techopsApi.put('/settings', settings); setSavedMsg('Saved'); setTimeout(() => setSavedMsg(''), 2000); }
    catch (e) { alert(e?.error || 'Failed'); }
  };

  const FIELD_LABELS = {
    category: 'CR Category', readiness: 'CR Readiness', contractors_vendors: 'Contractors & Vendors',
    mw_type: 'Maintenance Window Type', outage_type: 'Outage Type',
    outage_impact: 'Outage Service Main Impact', impacted_customers: 'Impacted Customers',
    result_obs_period: 'Result Observation Period', cr_status: 'CR Status',
    network_elements: 'Network Elements (Incident)', incident_impact: 'Impact (Incident)',
  };

  return (
    <div style={{ maxWidth: 1000 }}>
      <PageHead title="Administration" sub="Manage access, form dropdown options and app settings" />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['access', 'Access'], ['dropdowns', 'Dropdown Options'], ['settings', 'Settings']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...btn(tab === k ? BRAND : 'var(--surface2)', tab === k ? '#fff' : 'var(--text)') }}>{l}</button>
        ))}
      </div>

      {tab === 'settings' && (
        <div style={{ marginBottom: 30 }}>
          <div style={{ ...card, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>Change Request ID</div>
            <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 0, marginBottom: 12 }}>
              Assigned automatically to each new change request:
              <b> {(settings.cr_prefix || 'SCT_TCR')}_001_{new Date().getFullYear()}</b>
            </p>
            <input style={{ ...inp, maxWidth: 220 }} value={settings.cr_prefix || ''} placeholder="SCT_TCR"
              onChange={e => setSettings(s => ({ ...s, cr_prefix: e.target.value }))} />
          </div>

          <div style={{ ...card, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>Incident Report ID</div>
            <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 0, marginBottom: 12 }}>
              Assigned automatically to each new incident report:
              <b> {(settings.incident_prefix || 'SCT_INC')}_001_{new Date().getFullYear()}</b>
            </p>
            <input style={{ ...inp, maxWidth: 220 }} value={settings.incident_prefix || ''} placeholder="SCT_INC"
              onChange={e => setSettings(s => ({ ...s, incident_prefix: e.target.value }))} />
          </div>

          <div style={{ ...card, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 6 }}>Maintenance Window ID</div>
            <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 0, marginBottom: 12 }}>
              Assigned automatically to each new change request:
              <b> {(settings.mw_prefix || 'MW')}-{new Date().getFullYear()}-001</b>
            </p>
            <input style={{ ...inp, maxWidth: 220 }} value={settings.mw_prefix || ''} placeholder="MW"
              onChange={e => setSettings(s => ({ ...s, mw_prefix: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={saveSettings} style={btn()}>Save Settings</button>
            {savedMsg && <span style={{ fontSize: 13, color: '#16A34A', fontWeight: 700 }}>{savedMsg}</span>}
          </div>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
            Changing a prefix only affects records created from now on — existing references stay as they are.
          </p>
        </div>
      )}

      {tab === 'access' && (
        <div style={{ ...card, marginBottom: 30 }}>
          <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 0, marginBottom: 14 }}>
            <b>Admin</b> can edit any request, delete a request at any status (the deletion is kept in the
            history log) and configure the app. <b>Member</b> creates their own requests, edits/deletes their
            own drafts, and can see all requests in the department.
          </p>
          {access === null ? <div style={{ color: 'var(--muted)' }}>Loading…</div> : access.map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 13px', background: 'var(--surface2)', borderRadius: 9, marginBottom: 6, gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.full_name}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
              </div>
              {u.is_super ? <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', whiteSpace: 'nowrap' }}>Super Admin · full access</span> : (
                <select value={u.techops_role} disabled={savingId === u.id} onChange={e => setRole(u.id, e.target.value)} style={{ ...inp, width: 'auto', fontWeight: 700 }}>
                  <option value="none">No access</option>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'dropdowns' && (
        <div style={{ marginBottom: 30 }}>
          <p style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 0 }}>Configure the options available in each dropdown on the Change Request form.</p>
          {Object.keys(FIELD_LABELS).map(field => (
            <div key={field} style={{ ...card, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 10 }}>{FIELD_LABELS[field]}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {(options[field] || []).map(op => (
                  <span key={op.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 99, padding: '5px 11px', fontSize: 12 }}>
                    {op.value}<button onClick={() => delOpt(op.id)} style={{ border: 'none', background: 'none', color: '#DC2626', cursor: 'pointer', fontWeight: 700, fontSize: 14, lineHeight: 1 }}>×</button>
                  </span>
                ))}
                {(!options[field] || !options[field].length) && <span style={{ fontSize: 12, color: 'var(--muted)' }}>No options yet</span>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...inp, maxWidth: 260 }} value={newOpt[field] || ''} onChange={e => setNewOpt(p => ({ ...p, [field]: e.target.value }))} placeholder={`Add ${FIELD_LABELS[field]} option…`} onKeyDown={e => e.key === 'Enter' && addOpt(field)} />
                <button onClick={() => addOpt(field)} style={btn()}>Add</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
